import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import { SortOrder, SortOrderExtended } from "interfaces/SortOrder";
import { checkSortingType, reverseSortOrderValue } from "utils/sort-utils";
import { getPersonFullName } from "utils/person";
import { ISelectedHorsesWithRiskFactors } from "./ConfirmSelectionsDialog/ConfirmSelectionsDialog";

export enum WorkoutsTableTab {
  REQUESTS = "requests",
  EXAMS = "exams",
  PASSED = "passed",
  FAILED = "failed",
  REJECTED = "rejected"
}

export interface WorkoutsTabsStatuses {
  isRequestsTab: boolean;
  isExamsTab: boolean;
  isPassedTab: boolean;
  isFailedTab: boolean;
  isRejectedTab: boolean;
}

export const WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY =
  "workout-requests-total-result-query-key" as const;

export const checkCurrentWorkoutsTableTab = (
  tab: WorkoutsTableTab
): WorkoutsTabsStatuses => {
  return {
    isRequestsTab: tab === WorkoutsTableTab.REQUESTS,
    isExamsTab: tab === WorkoutsTableTab.EXAMS,
    isPassedTab: tab === WorkoutsTableTab.PASSED,
    isFailedTab: tab === WorkoutsTableTab.FAILED,
    isRejectedTab: tab === WorkoutsTableTab.REJECTED
  };
};

export const getUniqueCheckedHorses = (
  workoutsRequests: racehorse360.IWorkoutRequest[]
): ISelectedHorsesWithRiskFactors[] => {
  const uniqueCheckedHorsesIds = new Set();

  return workoutsRequests
    .filter(workoutRequest => {
      const shouldPass =
        workoutRequest.horse.riskFactorsCount &&
        !uniqueCheckedHorsesIds.has(workoutRequest.horse.id);

      if (shouldPass) {
        uniqueCheckedHorsesIds.add(workoutRequest.horse.id);

        return true;
      }

      return false;
    })
    .map(workoutRequest => ({
      horseId: workoutRequest.horse.id,
      horseName: workoutRequest.horse.name,
      riskFactorsCount: workoutRequest.horse.riskFactorsCount,
      riskFactorScore: workoutRequest.horse.riskFactorScore,
      trainerName: getPersonFullName(
        {
          firstName: workoutRequest.horse.trainer?.firstName,
          lastName: workoutRequest.horse.trainer?.lastName
        },
        "%f %L"
      )
    }));
};

export const getOrderRules = (
  orderByOptions: string[],
  order: SortOrder
): string[] => {
  const defaultRules = [`horse.name ${SortOrder.ASC}`, `date ${SortOrder.ASC}`];

  const isSortingInverted = [
    "horse.lastRaceDate",
    "horse.lastWorkoutDate"
  ].some(sortType => orderByOptions.includes(sortType));
  const actualOrder = isSortingInverted ? reverseSortOrderValue(order) : order;
  const { isOneOfAsc } = checkSortingType(actualOrder);

  const isSortingNullLast = [
    "horse.healthWatchStatus",
    "horse.barn.name",
    "horse.lastExamDate"
  ].some(sortType => orderByOptions.includes(sortType));
  const isDefaultAccordionView = [
    orderByOptions.length === 1,
    orderByOptions.includes("horse.trainer.lastName")
  ].every(Boolean);

  let originalRules: string[] = [];

  if (isSortingNullLast) {
    originalRules = orderByOptions.map(opt => {
      return `${opt} ${
        isOneOfAsc
          ? SortOrderExtended.ASC_NULLS_LAST
          : SortOrderExtended.DESC_NULLS_LAST
      }`;
    });
  } else if (isDefaultAccordionView) {
    originalRules = orderByOptions.map(opt => {
      return `${opt} ${
        isOneOfAsc
          ? SortOrderExtended.ASC_NULLS_FIRST
          : SortOrderExtended.DESC_NULLS_FIRST
      }`;
    });
  } else {
    originalRules = orderByOptions.map(opt => `${opt} ${actualOrder}`);
  }

  let additionalRules: string[] = [];

  if (orderByOptions.includes("isFlagged")) {
    additionalRules = [`flag.priority ${actualOrder}`];
  } else if (isDefaultAccordionView) {
    additionalRules = [
      `isFlagged ${SortOrder.DESC}`,
      `flag.priority ${SortOrder.DESC}`,
      `horse.healthWatchStatus ${SortOrderExtended.DESC_NULLS_LAST}`
    ];
  } else if (orderByOptions.includes("horse.barn.name")) {
    additionalRules = [
      `horse.trainer.lastName ${SortOrderExtended.ASC_NULLS_LAST}`
    ];
  }

  return [...originalRules, ...additionalRules, ...defaultRules];
};

export const getOrderByOptions = (
  options: string[] = [],
  orderBy: string
): string[] => {
  if (orderBy === "horse.name" || orderBy === "horse.trainer.lastName") {
    options = options.filter(item => item === orderBy);
  }

  return options;
};
